import React from 'react'
import {
  Button,
  Input,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Pagination,
  Badge,
  StatCards,
  Checkbox,
  Skeleton,
} from '@/shared/ui'
const LazySubscriptionCard = React.lazy(() => import('../components/SubscriptionCard'))
import { CreditCard, TrendingUp, Timer, AlertTriangle, Search } from 'lucide-react'
import { listSubscriptions } from '../services/subscriptionService'
import type { SubscriptionItem } from '../types'
import { format } from '@/shared/utils/date'
import { BulkSelectBar } from '@/shared/ui/BulkSelectBar'
import { AlertModal } from '@/shared/ui/AlertModal'
import { deleteCustomersBulk, inactivateCustomer } from '@/modules/customer/services/customerService'
import { listAccountInvoices, type InvoiceHistoryRow } from '../services/subscriptionHistoryService'
import { getSubscriptionStats, type SubscriptionStats } from '../services/subscriptionStatsService'
import { formatSubscriptionCurrency } from '../utils/format'

export default function SubscriptionsPage() {
  const [items, setItems] = React.useState<SubscriptionItem[]>([])
  const [total, setTotal] = React.useState(0)
  const [page, setPage] = React.useState(1)
  const [pageSize] = React.useState(25)
  const [search, setSearch] = React.useState('')
  const [status, setStatus] = React.useState('')
  const [loading, setLoading] = React.useState(true)
  const [selected, setSelected] = React.useState<Set<string>>(new Set())
  const [confirmOpen, setConfirmOpen] = React.useState(false)
  const [deleting, setDeleting] = React.useState(false)
  const [expanded, setExpanded] = React.useState<Set<string>>(new Set())
  const [histories, setHistories] = React.useState<Record<string, { rows: InvoiceHistoryRow[]; total: number }>>({})
  const [agg, setAgg] = React.useState<SubscriptionStats>({
    total: 0,
    approved: 0,
    pending: 0,
    past_due: 0,
    inactive: 0,
    trial: 0,
  })
  const [visibleCount] = React.useState(10)
  const loadMoreRef = React.useRef<HTMLDivElement | null>(null)
  const CHUNK = 10

  async function load(withStats = false) {
    setLoading(true)
    try {
      const subsRes = await listSubscriptions({ search, status, page, pageSize })
      setItems(subsRes.items)
      setTotal(subsRes.total)
      if (withStats) {
        const statsRes = await getSubscriptionStats()
        setAgg(statsRes)
      }
    } finally {
      setLoading(false)
    }
  }

  React.useEffect(() => {
    load(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    load(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, status])

  const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize))

  // Agrupa por conta e escolhe a melhor assinatura por status / período
  const groups = React.useMemo(() => {
    const rank = (st?: string | null) => {
      const raw = String(st || '').toLowerCase()
      if (raw === 'active') return 3
      if (raw === 'trialing') return 2
      if (raw === 'past_due') return 1
      return 0
    }

    const byAcc = new Map<string, SubscriptionItem[]>()
    for (const s of items) {
      const arr = byAcc.get(s.account_id) || []
      arr.push(s)
      byAcc.set(s.account_id, arr)
    }

    const best: SubscriptionItem[] = []
    byAcc.forEach((arr) => {
      arr.sort((a, b) => {
        const ra = rank(a.status)
        const rb = rank(b.status)
        if (ra !== rb) return rb - ra
        const da = a.current_period_end || a.created_at || ''
        const db = b.current_period_end || b.created_at || ''
        return db.localeCompare(da)
      })
      best.push(arr[0])
    })

    best.sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')))
    return best
  }, [items])

  // (lazy render desativado para simplificar: renderiza todos os itens)

  async function toggleExpand(accId: string) {
    setExpanded((prev) => {
      const n = new Set(prev)
      if (n.has(accId)) n.delete(accId)
      else n.add(accId)
      return n
    })
    if (!histories[accId]) {
      try {
        const h = await listAccountInvoices(accId, 1, 10)
        setHistories((m) => ({ ...m, [accId]: h }))
      } catch {
        // ignore
      }
    }
  }

  const stats = React.useMemo(
    () => [
      {
        label: 'Assinaturas',
        value: agg.total.toLocaleString('pt-BR'),
        icon: <CreditCard className="h-5 w-5" />,
        tone: 'primary' as const,
        onClick: () => {
          setStatus('')
          setPage(1)
        },
        active: status === '',
      },
      {
        label: 'Aprovadas',
        value: agg.approved.toLocaleString('pt-BR'),
        icon: <TrendingUp className="h-5 w-5" />,
        tone: 'success' as const,
        onClick: () => {
          setStatus('active')
          setPage(1)
        },
        active: status === 'active',
      },
      {
        label: 'Pendentes',
        value: agg.pending.toLocaleString('pt-BR'),
        icon: <Timer className="h-5 w-5" />,
        tone: 'warning' as const,
        onClick: () => {
          setStatus('incomplete')
          setPage(1)
        },
        active: status === 'incomplete',
      },
      {
        label: 'Inativos',
        value: agg.inactive.toLocaleString('pt-BR'),
        icon: <AlertTriangle className="h-5 w-5" />,
        tone: 'violet' as const,
        onClick: () => {
          setStatus('canceled')
          setPage(1)
        },
        active: status === 'canceled',
      },
      {
        label: 'Trial',
        value: agg.trial.toLocaleString('pt-BR'),
        icon: <Timer className="h-5 w-5" />,
        tone: 'warning' as const,
        onClick: () => {
          setStatus('trialing')
          setPage(1)
        },
        active: status === 'trialing',
      },
    ],
    [agg, status],
  )

  return (
    <div className="mx-auto w-full max-w-[1550px]">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Assinaturas</h1>
        <p className="text-sm text-foreground/70">Visão geral de todas as assinaturas do sistema.</p>
      </div>

      <StatCards items={stats} className="mb-6 lg:grid-cols-5" />

      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="relative w-full max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground/60" />
          <Input
            placeholder="Buscar nome, e-mail ou plano"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-2">
          <Select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Todos</option>
            <option value="active">Ativas</option>
            <option value="trialing">Em teste</option>
            <option value="incomplete">Incompletas</option>
            <option value="past_due">Vencidas</option>
            <option value="canceled">Canceladas</option>
          </Select>
          <Button onClick={() => load(true)} disabled={loading}>
            Atualizar
          </Button>
        </div>
      </div>

      <BulkSelectBar
        selectedCount={selected.size}
        totalCount={groups.length}
        onSelectPage={() => setSelected(new Set(groups.map((i) => i.account_id)))}
        onClear={() => setSelected(new Set())}
        onDelete={() => setConfirmOpen(true)}
        className="mb-3"
      />

      {/* Cabeçalho visual (grid) + cards */}
      <div className="grid grid-cols-[minmax(260px,1.2fr)_minmax(180px,240px)_minmax(120px,160px)_minmax(280px,1.2fr)_minmax(120px,140px)_42px] gap-4 px-2 pb-2 text-xs text-foreground/70">
        <div>Cliente</div>
        <div>Plano</div>
        <div>Intervalo</div>
        <div>Datas</div>
        <div>Status</div>
        <div className="text-right">Ações</div>
      </div>
      <div className="space-y-3">
        {groups.map((s) => (
          <React.Suspense
            key={s.id}
            fallback={
              <div className="rounded-xl border border-[#161616] bg-background/40 p-4">
                <Skeleton className="h-20 w-full" />
              </div>
            }
          >
            <LazySubscriptionCard
              item={s}
              checked={selected.has(s.account_id)}
              onToggle={(ck) =>
                setSelected((prev) => {
                  const n = new Set(prev)
                  if (ck) n.add(s.account_id)
                  else n.delete(s.account_id)
                  return n
                })
              }
              expanded={expanded.has(s.account_id)}
              onToggleExpand={() => toggleExpand(s.account_id)}
              history={histories[s.account_id]}
            />
          </React.Suspense>
        ))}
        {/* Sentinel removido: renderização completa */}
        {groups.length === 0 && (
          <div className="p-6 text-center text-sm text-foreground/70">
            {loading ? 'Carregando...' : 'Nenhuma assinatura encontrada.'}
          </div>
        )}
      </div>

      <div className="mt-4 flex justify-end">
        <Pagination page={page} total={totalPages} onChange={setPage} />
      </div>

      <AlertModal
        open={confirmOpen}
        onClose={() => !deleting && setConfirmOpen(false)}
        title="Excluir clientes selecionados?"
        description={
          <div className="space-y-2">
            <p>
              Você está prestes a excluir <strong>{selected.size}</strong> cliente(s) com base nas assinaturas
              selecionadas.
            </p>
            <ul className="list-disc pl-5 text-sm text-foreground/80">
              <li>As assinaturas ativas serão finalizadas imediatamente (status cancelado).</li>
              <li>As contas, perfis e usuários do Supabase Auth serão removidos.</li>
              <li>Ação irreversível.</li>
            </ul>
          </div>
        }
        variant="warning"
        confirmText={deleting ? 'Excluindo...' : 'Confirmar exclusão'}
        onConfirm={async () => {
          if (deleting) return
          setDeleting(true)
          try {
            const accIds = Array.from(selected)
            for (const id of accIds) {
              try {
                await inactivateCustomer(id)
              } catch {
                // ignore single failures
              }
            }
            await deleteCustomersBulk(accIds)
            setSelected(new Set())
            await load()
          } finally {
            setDeleting(false)
            setConfirmOpen(false)
          }
        }}
      />
    </div>
  )
}
